/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from 'context/WebSocketConnectionContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { MEDICAL_RECORD_DEMOTE_COMPLETED_TOAST } from 'modules/FileManagerV2/constants/medicalRecordPromotion';

const WS_EVENT_TYPE = 'medical_chronology_update';

const COMPLETED_EVENTS = new Set(['chronology_compilation_complete']);
const EXHIBIT_REFRESH_EVENTS = new Set(['exhibit_processed', 'exhibit_processing_complete', 'exhibit_processing_failed']);
const FAILURE_EVENTS = new Set(['compilation_failed', 'exhibit_processing_failed']);
const DEMOTION_TERMINAL_EVENTS = new Set(['healthlake_demotion_completed', 'healthlake_demotion_failed']);

export const IN_PROGRESS_TOAST_MESSAGE = "Medical Chronology analysis may take some time. We'll update you when it's complete.";

export const COMPLETION_TOAST_MESSAGE = 'Medical Chronology generated successfully';

export const FAILURE_TOAST_MESSAGE = 'Medical Chronology processing failed';

const getExhibitCacheList = (cached) => {
  if (Array.isArray(cached)) return cached;
  if (Array.isArray(cached?.data)) return cached.data;
  return [];
};

/**
 * True when any paginated exhibits cache for the chronology includes fileId.
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} chronologyId
 * @param {string} fileId
 * @returns {boolean}
 */
export const exhibitCacheHasFileId = (queryClient, chronologyId, fileId) => {
  if (!fileId || !chronologyId) return false;
  const entries = queryClient.getQueriesData(['medchron-exhibits', chronologyId]);
  return entries.some(([, cached]) => getExhibitCacheList(cached).some((exhibit) => exhibit?.file_id === fileId));
};

const normalizeCompilationStatus = (status) => {
  if (status === 'in_progress') return 'processing';
  return status ?? null;
};

const setCompileStatusCache = (queryClient, chronologyId, status, extra = {}) => {
  const normalizedStatus = normalizeCompilationStatus(status);
  if (!normalizedStatus) return;

  queryClient.setQueryData(['medchron-compile-status', chronologyId], (current = {}) => ({
    ...current,
    ...extra,
    compilation_status: normalizedStatus,
    status: normalizedStatus,
  }));
};

const invalidateCompiledChronologyQueries = (queryClient, chronologyId) => {
  queryClient.invalidateQueries(['medchron-overview', chronologyId]);
  queryClient.invalidateQueries(['medchron-record']);
};

/**
 * Marks exhibit queries stale and refetches active observers so Sources-tab rows
 * update without a manual page refresh.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} chronologyId
 */
export const refreshExhibitsCache = (queryClient, chronologyId) => {
  const queryKey = ['medchron-exhibits', chronologyId];
  queryClient.invalidateQueries(queryKey);
  queryClient.refetchQueries({ queryKey, type: 'active' });
};

/**
 * Subscribes to Medical Chronology WebSocket events and dispatches React Query
 * cache operations (invalidateQueries / setQueryData) for each of the four
 * BE notification event types. Cleans up on unmount.
 *
 * @param {string} chronologyId
 */
const useChronologyWebSocket = (chronologyId) => {
  const queryClient = useQueryClient();
  const { subscribe, unsubscribe } = useWebSocket();
  const { addToast } = useToast();
  const inProgressToastShownRef = useRef(false);

  useEffect(() => {
    if (!chronologyId) return;

    inProgressToastShownRef.current = false;

    const handler = (data) => {
      const { event, chronology_id: incomingId, file_id: bodyFileId, data: eventData } = data?.body ?? {};
      const resolvedChronologyId = incomingId ?? eventData?.chronology_id;
      const fileId = bodyFileId ?? eventData?.file_id;

      // Demotion terminal events may omit chronology_id — match by file_id in cache (ADR-005)
      if (DEMOTION_TERMINAL_EVENTS.has(event)) {
        const chronologyMatches = resolvedChronologyId === chronologyId;
        const fileMatches = exhibitCacheHasFileId(queryClient, chronologyId, fileId);
        if (chronologyMatches || fileMatches) {
          refreshExhibitsCache(queryClient, chronologyId);
          if (event === 'healthlake_demotion_completed') {
            queryClient.invalidateQueries(['medchron-record']);
            // Active calendar observers refetch POST /list-events-v2 after demotion deletes extracted events.
            queryClient.invalidateQueries(['medchron-calendar-events-v2']);
            queryClient.invalidateQueries(['medchron-calendar-day-events']);
            addToast(false, toastConstant.toasterType.SUCCESS, 'Success', MEDICAL_RECORD_DEMOTE_COMPLETED_TOAST);
          }
        }
        return;
      }

      if (resolvedChronologyId !== chronologyId) return;

      const incomingCompilationStatus = normalizeCompilationStatus(eventData?.compilation_status);
      if (incomingCompilationStatus) {
        setCompileStatusCache(queryClient, chronologyId, incomingCompilationStatus, {
          last_event: event,
          ...(eventData?.job_id != null ? { job_id: eventData.job_id } : {}),
          ...(eventData?.compiled_count != null ? { compiled_count: eventData.compiled_count } : {}),
        });
      }

      switch (event) {
        case 'exhibit_processed':
        case 'exhibit_processing_complete':
        case 'exhibit_processing_failed':
          refreshExhibitsCache(queryClient, chronologyId);
          if (FAILURE_EVENTS.has(event) || incomingCompilationStatus === 'failed') {
            console.warn('medchron exhibit processing failed:', eventData?.message);
            addToast(false, toastConstant.toasterType.ERROR, 'Error', eventData?.message || 'Exhibit processing failed.');
          } else if (
            event === 'exhibit_processing_complete' &&
            incomingCompilationStatus === 'processing' &&
            !inProgressToastShownRef.current
          ) {
            inProgressToastShownRef.current = true;
            addToast(false, toastConstant.toasterType.INFO, 'Info', IN_PROGRESS_TOAST_MESSAGE);
          }
          if (process.env.NODE_ENV === 'development') {
            console.debug('medchron exhibits refreshed:', event, chronologyId);
          }
          break;

        case 'chronology_compilation_complete':
          // Prefer setQueryData over compile-status GET invalidation so a lagged
          // GET cannot overwrite the completed status written from this event.
          setCompileStatusCache(queryClient, chronologyId, 'completed', {
            last_event: event,
            ...(eventData?.job_id != null ? { job_id: eventData.job_id } : {}),
            ...(eventData?.compiled_count != null ? { compiled_count: eventData.compiled_count } : {}),
          });
          refreshExhibitsCache(queryClient, chronologyId);
          invalidateCompiledChronologyQueries(queryClient, chronologyId);
          inProgressToastShownRef.current = false;
          addToast(false, toastConstant.toasterType.SUCCESS, 'Success', COMPLETION_TOAST_MESSAGE);
          if (process.env.NODE_ENV === 'development') {
            console.debug('medchron compilation successful:', event, chronologyId);
          }
          break;

        case 'compilation_failed':
          setCompileStatusCache(queryClient, chronologyId, 'failed', {
            last_event: event,
          });
          inProgressToastShownRef.current = false;
          console.warn('medchron compilation failed:', eventData?.message);
          addToast(false, toastConstant.toasterType.ERROR, 'Error', eventData?.message || FAILURE_TOAST_MESSAGE);
          break;

        case 'pdf_ready':
          queryClient.setQueryData(['medchron-pdf-status', chronologyId], {
            status: 'completed',
            file_id: eventData?.file_id,
            file_sk: eventData?.file_sk,
            file_path: eventData?.file_path,
            exported_at: eventData?.exported_at || new Date().toISOString(),
            filename: eventData?.filename,
          });
          if (process.env.NODE_ENV === 'development') {
            console.debug('medchron cache invalidated:', event, chronologyId);
          }
          addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Chronology PDF is ready. Download will start automatically.');
          break;

        case 'error':
          // Log message text only — do not log full eventData (PHI guard)
          console.warn('medchron WS error:', eventData?.message);
          addToast(
            false,
            toastConstant.toasterType.ERROR,
            'Error',
            eventData?.message || 'An error occurred during chronology processing.'
          );
          break;

        default:
          if (incomingCompilationStatus === 'failed') {
            console.warn('medchron compilation failed:', eventData?.message);
            addToast(false, toastConstant.toasterType.ERROR, 'Error', eventData?.message || FAILURE_TOAST_MESSAGE);
            break;
          }
          if (COMPLETED_EVENTS.has(event)) {
            setCompileStatusCache(queryClient, chronologyId, 'completed', {
              last_event: event,
            });
            invalidateCompiledChronologyQueries(queryClient, chronologyId);
            break;
          }
          if (EXHIBIT_REFRESH_EVENTS.has(event)) {
            refreshExhibitsCache(queryClient, chronologyId);
            break;
          }
          if (process.env.NODE_ENV === 'development' && event) {
            console.debug('medchron WS event ignored:', event, chronologyId);
          }
          break;
      }
    };

    subscribe(WS_EVENT_TYPE, handler);
    return () => {
      unsubscribe(WS_EVENT_TYPE, handler);
    };
  }, [chronologyId]);
};

export default useChronologyWebSocket;
